import { IonIcon } from "@ionic/react";
import { useTranslation } from "react-i18next";
import { helpCircleOutline } from "ionicons/icons";
import { ModalSheetAutoHeight } from "@/components/modal/sheet/auto-height";
import { EmployeeHoursResponseSection } from "@/components/molecules/shift/employee-hours-response-section";
import { EntryStatusChip } from "@/components/molecules/shift/entry-status-chip";
import type { Shift } from "@/lib/backend/api";
import { canRespondToHours } from "@/lib/shift/employee-hours-response";
import { isSettled, toEntryState } from "@/lib/shift/entry-status";
import { formatTime } from "@/utils/format-datetime";

interface HoursPreviewModalProps {
  modalRef: React.RefObject<HTMLIonModalElement>;
  hoursInfoModalRef: React.RefObject<HTMLIonModalElement>;
  shift: Shift;
  durationDutch: string | null;
  /** Admin#624 — reageren op door de organisatie geüploade uren. */
  onRespond: (response: "AGREED" | "DISPUTED", note?: string) => void;
  isRespondingToHours: boolean;
}

export function HoursPreviewModal({
  modalRef,
  hoursInfoModalRef,
  shift,
  durationDutch,
  onRespond,
  isRespondingToHours,
}: HoursPreviewModalProps) {
  const { t } = useTranslation();
  const hoursState = toEntryState(shift.shiftTimeEntry?.status);

  // Eén waarheid voor de zin hieronder én de knoppen onderaan: wachten deze uren op de medewerker?
  //
  // `canRespondToHours` kent alleen de invoerder, geen status. Accepteerde het kantoor de uren,
  // dan is de zaak rond (`isSettled`) en zei de zin "Akkoord. Dit kan niet meer worden aangepast."
  // terwijl er werkende knoppen onder stonden. Dat is admin#1093 omgekeerd, en één variabele voor
  // allebei houdt ze bij elkaar.
  const awaitsResponse = canRespondToHours(shift.shiftTimeEntry) && !isSettled(hoursState);

  // Geklokte uren horen in projecttijd te staan — dezelfde klok waarop de medewerker ze invoerde.
  // Zonder zone las 15:00 op een telefoon in Toronto als 09:00.
  const timeZone = shift.timeZone ?? shift.project?.timeZone;

  return (
    <ModalSheetAutoHeight ref={modalRef}>
      <div className="flex flex-col gap-5 px-6 pb-6 pt-4">
        {/* Header with status badge and info icon */}
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold leading-[1.6] text-txt-primary">
              {t("shift:hours.title")}
            </h2>
            <button
              type="button"
              onClick={() => hoursInfoModalRef.current?.present()}
              className="flex size-12 items-center justify-center"
            >
              <IonIcon icon={helpCircleOutline} className="text-2xl text-txt-tertiary" />
            </button>
          </div>

          {/* Same chip as the row, so a status never reads one way in a list and another in the
              sheet you opened from it. The sentence underneath is the part a chip cannot carry:
              whether you still have to do something.

              En daar ging het mis (admin#1093): "Kantoor kijkt ernaar, je hoeft niets te doen"
              stond boven de Klopt/Klopt niet-knoppen. Bij uren die het kantoor invulde wacht de
              bal juist bij de medewerker. Dezelfde `awaitsResponse` als de knoppen hieronder,
              zodat de zin en de knoppen niet uit elkaar kunnen lopen. */}
          {hoursState && (
            <div className="flex flex-col gap-1.5">
              <EntryStatusChip kind="hours" status={shift.shiftTimeEntry?.status} />
              <p className="text-sm leading-[1.5] text-txt-secondary">
                {awaitsResponse && hoursState === "pending"
                  ? t("shift:entry-status.hint.pendingResponse")
                  : t(`shift:entry-status.hint.${hoursState}`)}
              </p>
            </div>
          )}
        </div>

        {/* Content - tightly grouped fields */}
        {shift.shiftTimeEntry && (
          <>
            <div className="flex flex-col gap-2">
              {/* Start time */}
              <div className="flex flex-col gap-1">
                <p className="text-base font-medium leading-[1.5] text-txt-tertiary">
                  {t("shift:hours.fields.startTime")}
                </p>
                <p className="text-base font-medium leading-[1.5] text-txt-primary">
                  {shift.shiftTimeEntry.startAt
                    ? formatTime(
                        typeof shift.shiftTimeEntry.startAt === "string"
                          ? shift.shiftTimeEntry.startAt
                          : shift.startAt,
                        { timeZone },
                      )
                    : "-"}
                </p>
              </div>

              {/* End time */}
              <div className="flex flex-col gap-1">
                <p className="text-base font-medium leading-[1.5] text-txt-tertiary">
                  {t("shift:hours.fields.endTime")}
                </p>
                <p className="text-base font-medium leading-[1.5] text-txt-primary">
                  {shift.shiftTimeEntry.endAt
                    ? formatTime(shift.shiftTimeEntry.endAt, { timeZone })
                    : "-"}
                </p>
              </div>

              {/* Break time */}
              <div className="flex flex-col gap-1">
                <p className="text-base font-medium leading-[1.5] text-txt-tertiary">
                  {t("shift:hours.fields.breakDuration")}
                </p>
                <p className="text-base font-medium leading-[1.5] text-txt-primary">
                  {shift.shiftTimeEntry.breakTimeMinutes !== null &&
                  shift.shiftTimeEntry.breakTimeMinutes !== undefined
                    ? `${Math.floor(shift.shiftTimeEntry.breakTimeMinutes / 60)}u ${shift.shiftTimeEntry.breakTimeMinutes % 60}m`
                    : "0u 0m"}
                </p>
              </div>

              {/* Total duration */}
              {durationDutch && (
                <div className="flex flex-col gap-1">
                  <p className="text-base font-medium leading-[1.5] text-txt-tertiary">
                    {t("shift:hours.fields.totalWorked")}
                  </p>
                  <p className="text-base font-medium leading-[1.5] text-txt-primary">
                    {durationDutch}
                  </p>
                </div>
              )}
            </div>

            {/* Klopt / klopt niet (admin#624) — alleen bij uren die de organisatie invoerde en
                die nog op de medewerker wachten. Bij eigen invoer valt niets te bevestigen (daar
                bestaat het opmerkingveld al), en na goedkeuring valt er niets meer te vragen. */}
            {awaitsResponse && (
              <EmployeeHoursResponseSection
                entry={shift.shiftTimeEntry}
                onRespond={onRespond}
                isSubmitting={isRespondingToHours}
              />
            )}

            {/* Close button */}
            <button
              type="button"
              onClick={() => modalRef.current?.dismiss()}
              className="w-full rounded-xl border border-solid border-bordercolor-primary py-3 text-center text-base font-semibold tracking-[0.08px] text-txt-primary"
            >
              {t("common:close")}
            </button>
          </>
        )}
      </div>
    </ModalSheetAutoHeight>
  );
}
